// packages/supabase-client/src/react/components/OAuthProviders.tsx

import React, { useState } from 'react';
import { Button } from '@dopomogai/ui';
import { useAuthErrorMessage, useAuthService, useAuthText, useAuthUI } from '../hooks';
import type { AuthUIProviderConfig } from '../ui';
import { AuthAlert } from './AuthAlert';

// Inline Google logo icon
const GoogleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="20" height="20" {...props}>
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.222,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C39.712,35.619,44,29.569,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
  </svg>
);

const defaultProviders: AuthUIProviderConfig[] = [
  {
    id: 'google',
    scopes: 'profile email',
    buttonVariant: 'outline',
  },
];

const getDefaultLabel = (providerId: AuthUIProviderConfig['id']) => {
  if (providerId === 'google') return 'Continue with Google';
  return `Continue with ${providerId}`;
};

const getDefaultIcon = (providerId: AuthUIProviderConfig['id']) => {
  if (providerId === 'google') return <GoogleIcon />;
  return undefined;
};

export interface OAuthProvidersProps {
  providers?: AuthUIProviderConfig[];
}

export const OAuthProviders: React.FC<OAuthProvidersProps> = ({ providers }) => {
  const authService = useAuthService();
  const { resolveErrorMessage } = useAuthErrorMessage();
  const { text, resolveText } = useAuthText();
  const { config, redirects } = useAuthUI();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<React.ReactNode | null>(null);

  const effectiveProviders = providers ?? (config?.providers ?? defaultProviders);

  if (!effectiveProviders.length) {
    return null;
  }

  const handleProviderSignIn = async (provider: AuthUIProviderConfig) => {
    setIsPending(true);
    setError(null);

    const result = await authService.signInWithProvider(provider.id, {
      scopes: provider.scopes,
      redirectTo: provider.redirectTo ?? redirects.oauthRedirectTo,
    });

    if (!result.success) {
      setError(resolveErrorMessage(result.error));
      setIsPending(false);
    }
    // on success, Supabase will redirect
  };

  return (
    <div className="space-y-4">
      {effectiveProviders.map((provider) => {
        const defaultLabel = provider.id === 'google'
          ? resolveText(text.continueWithGoogle, 'auth:continueWithGoogle', { defaultValue: getDefaultLabel(provider.id) })
          : getDefaultLabel(provider.id);
        const label = provider.label ?? defaultLabel;
        const labelIsString = typeof label === 'string' || typeof label === 'number';
        const icon = provider.icon ?? getDefaultIcon(provider.id);

        return (
          <Button
            key={provider.id}
            variant={provider.buttonVariant ?? 'outline'}
            fullWidth
            onClick={() => handleProviderSignIn(provider)}
            disabled={isPending}
            icon={icon}
            text={labelIsString ? String(label) : undefined}
            loading={isPending}
          >
            {!labelIsString ? label : null}
          </Button>
        );
      })}
      {error && <AuthAlert message={error} className="text-center" />}
    </div>
  );
};
